import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { placeholderImage, toPublicStorageUrl } from "../config/imageConfig";
import { useNavigate, useLocation } from "react-router-dom";
import BackButton from "./BackButton";
import { getProductDetails } from "../api/searchApi";
import { useCart } from "../context/CartContext";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Divider,
  Stack,
  IconButton,
  Rating,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SecurityIcon from "@mui/icons-material/Security";
import RefreshIcon from "@mui/icons-material/Refresh";
import ShareIcon from "@mui/icons-material/Share";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import WishlistToggle from "./WishlistToggle";
import TryOnStage from "./TryOnStage";
import morphLayout from "../utils/morphLayout";
import { registerInlineTryOn } from "../utils/inlineTryOn";
import { trackProductViewed } from "../services/uiAgentSync";
import { getColorHex } from "../utils/colorHex";

// Styled Components
const ThumbnailImage = styled(Box)(({ theme, selected }) => ({
  width: 60,
  height: 80,
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
  cursor: "pointer",
  border: selected
    ? `3px solid ${theme.palette.grey[900]}`
    : "3px solid transparent",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    border: `3px solid ${theme.palette.grey[900]}`,
    transform: "scale(1.05)",
  },
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
}));

const MainImageContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  position: "relative",
  borderRadius: theme.shape.borderRadius * 1.5,
  overflow: "hidden",
  backgroundColor: "#f5f5f5",
  height: 500,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  "&:hover": {
    opacity: 0.95,
  },
}));

const MainImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "contain",
  pointerEvents: "none", // Allow clicks to pass through to container
});

const NavButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  backgroundColor: "white",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  "&:hover": {
    backgroundColor: theme.palette.grey[100],
  },
  zIndex: 2,
}));

const SizeButton = styled(Button)(({ theme, selected }) => ({
  minWidth: 80,
  padding: "12px 20px",
  border: selected
    ? `2px solid ${theme.palette.grey[900]}`
    : `1px solid ${theme.palette.grey[300]}`,
  backgroundColor: selected ? theme.palette.grey[900] : "white",
  color: selected ? "white" : theme.palette.grey[800],
  borderRadius: 4,
  fontSize: "0.875rem",
  fontWeight: 500,
  "&:hover": {
    backgroundColor: selected
      ? theme.palette.grey[800]
      : theme.palette.grey[50],
    border: `2px solid ${theme.palette.grey[900]}`,
  },
}));

const ColorCircle = styled(Box)(({ theme, selected, colorvalue }) => ({
  width: 32,
  height: 32,
  borderRadius: "50%",
  background: colorvalue || theme.palette.grey[300],
  border: selected
    ? `3px solid ${theme.palette.grey[900]}`
    : "2px solid #e0e0e0",
  cursor: "pointer",
  transition: "all 0.2s ease-in-out",
  position: "relative",
  "&:hover": {
    transform: "scale(1.1)",
    border: `3px solid ${theme.palette.grey[900]}`,
  },
  "&::after": selected
    ? {
        content: '""',
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: "white",
        border: "1px solid rgba(0,0,0,0.3)",
      }
    : {},
}));

const ProductCard = styled(Card)(({ theme }) => ({
  height: "100%",
  transition: "transform 0.2s, box-shadow 0.2s",
  cursor: "pointer",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[4],
  },
}));

const QuantitySelector = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.shape.borderRadius,
}));

const QuantityButton = styled(IconButton)(({ theme }) => ({
  borderRadius: 0,
  padding: "8px 12px",
  "&:hover": {
    backgroundColor: theme.palette.grey[100],
  },
}));

// Utility to clean image URLs
const cleanImageUrl = (url) => {
  if (typeof url !== "string") return "";
  return toPublicStorageUrl(url.trim());
};

export default function ProductDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    product: initialProduct,
    attributionToken: passedAttributionToken,
    searchQuery: passedSearchQuery,
    correctedQuery: passedCorrectedQuery,
  } = location.state || {};

  const { cartItems, addToCart } = useCart();
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [previewQuantity, setPreviewQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Virtual Try-On state
  const [tryOnOpen, setTryOnOpen] = useState(false);
  // Inline try-on: `autoStart` is the agent's mode; `tryOnSession` remounts
  // the mirror so a second agent request starts a fresh render.
  const [tryOnAutoStart, setTryOnAutoStart] = useState(null);
  const [tryOnSession, setTryOnSession] = useState(0);
  // Opening and closing re-lay the page (photo and mirror side by side,
  // details below), so both run as an animated morph.
  const openTryOn = useCallback((autoStart = null) => {
    morphLayout(() => {
      setTryOnAutoStart(autoStart);
      setTryOnSession((n) => n + 1);
      setTryOnOpen(true);
    });
  }, []);
  const closeTryOn = useCallback(() => {
    morphLayout(() => setTryOnOpen(false));
  }, []);

  const productId = window.location.pathname.split("/").pop();
  // Let an agent-started try-on for this product open here, inline, instead
  // of in the fallback popup.
  useEffect(
    () =>
      registerInlineTryOn(productId, {
        open: ({ autoStart }) => openTryOn(autoStart),
        close: closeTryOn,
      }),
    [productId, openTryOn, closeTryOn]
  );

  // Check if already in cart (by id + size)
  const existingCartItem = useMemo(() => {
    if (!productId || !selectedSize) return null;
    return cartItems.find(
      (item) => item.id === productId && item.size === selectedSize
    );
  }, [cartItems, productId, selectedSize]);

  const isInCart = !!existingCartItem;
  const quantity = isInCart ? existingCartItem.quantity : previewQuantity;
  const isAlreadyInCart = isInCart;

  const [product, setProduct] = useState(initialProduct || null);
  const [loading, setLoading] = useState(!initialProduct);
  const [error, setError] = useState("");

  // UI → Agent sync: report the product view once per product id
  const trackedProductIdRef = useRef(null);
  useEffect(() => {
    if (!product || !productId) return;
    if (trackedProductIdRef.current === productId) return;
    trackedProductIdRef.current = productId;
    trackProductViewed({
      productId,
      productTitle: product.title ?? product.name ?? null,
    });
  }, [product, productId]);

  useEffect(() => {
    setProduct(null);
    setLoading(true);
    setError("");
    setCurrentImageIndex(0);
    setSelectedSize("");
    setSelectedColor("");

    const loadProduct = async () => {
      try {
        let productData = null;

        if (initialProduct && initialProduct.id === productId) {
          productData = initialProduct;
        } else {
          const searchQueryToUse =
            passedCorrectedQuery || passedSearchQuery || "";
          productData = await getProductDetails(
            productId,
            passedAttributionToken,
            searchQueryToUse,
            true,
            passedCorrectedQuery
          );
        }

        setProduct(productData);
      } catch (err) {
        setError(err.message || "Failed to load product details.");
      } finally {
        setLoading(false);
      }
    };

    if (productId && passedAttributionToken) {
      loadProduct();
    } else if (!passedAttributionToken) {
      setError("Missing attribution token. Please go back and try again.");
      setLoading(false);
    }
  }, [productId, passedAttributionToken, initialProduct]);

  // Collect all images from product and variants
  const allImages = useMemo(() => {
    const images = [];

    if (product?.product?.images) {
      product.product.images.forEach((img) => {
        images.push({
          url: img,
          id: product.product.id,
          label: "Main",
        });
      });
    }

    if (product?.variants) {
      product.variants.forEach((variant) => {
        if (variant.images) {
          variant.images.forEach((img) => {
            images.push({
              url: img,
              id: variant.id,
              label: variant.attributes?.color || "Variant",
            });
          });
        }
      });
    }

    return images;
  }, [product]);

  // Get colors from variants
  const colorList = useMemo(() => {
    const colors = [];
    if (product?.variants) {
      product.variants.forEach((variant) => {
        if (variant.attributes?.color) {
          colors.push(variant.attributes.color);
        }
      });
    }
    if (colors.length === 0 && product?.product?.attributes?.color) {
      const productColors = String(product.product.attributes.color)
        .split(",")
        .map((c) => c.trim())
        .filter(Boolean);
      colors.push(...productColors);
    }
    return colors;
  }, [product]);

  // Extract sizes
  const sizeList = useMemo(() => {
    const sizes = product?.product?.attributes?.size;
    if (!sizes) return [];
    if (Array.isArray(sizes)) return sizes;
    if (typeof sizes === "string") {
      return sizes
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return [];
  }, [product]);

  // Price & rating
  const primaryProduct = product?.product || {};
  const currentVariant = product?.variants?.[0] || {};
  const priceInfo =
    currentVariant.price_info || primaryProduct.price_info || {};
  const originalPrice = parseFloat(
    currentVariant.original_price ||
      priceInfo.originalPrice ||
      primaryProduct.price ||
      0
  );
  const displayPrice = parseFloat(
    currentVariant.discounted_price || priceInfo.price || originalPrice
  );
  const isOnSale = originalPrice > displayPrice && displayPrice > 0;

  const rating = parseFloat(primaryProduct.attributes?.rating) || 4.9;
  const reviewCount = parseInt(primaryProduct.attributes?.review_count) || 89;

  // Handle Add to Cart
  const handleAddToCart = () => {
    if (!selectedSize) {
      alert("Please select a size before adding to cart.");
      return;
    }

    if (!product?.product) {
      alert("Product data is missing.");
      return;
    }

    const productItem = product.product;

    if (existingCartItem) {
      addToCart({ ...existingCartItem, quantity });
      return;
    }

    addToCart({
      id: productId,
      title: productItem.title || "Product",
      price: displayPrice,
      currency: priceInfo.currencyCode || "USD",
      description: productItem.description || "",
      image: productItem.images?.[0]
        ? cleanImageUrl(productItem.images[0])
        : "",
      size: selectedSize,
      color: selectedColor,
      variants: product?.variants || [],
      quantity: previewQuantity,
    });
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? allImages.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === allImages.length - 1 ? 0 : prev + 1
    );
  };

  const incrementQuantity = () => {
    if (isInCart) {
      // Quantity stepper, not an add — no toast, no fly-to-bag.
      addToCart(
        {
          ...existingCartItem,
          quantity: existingCartItem.quantity + 1,
        },
        { silent: true }
      );
    } else {
      setPreviewQuantity((prev) => prev + 1);
    }
  };

  const decrementQuantity = () => {
    if (isInCart) {
      if (existingCartItem.quantity > 1) {
        addToCart(
          {
            ...existingCartItem,
            quantity: existingCartItem.quantity - 1,
          },
          { silent: true }
        );
      } else {
        addToCart({ ...existingCartItem, quantity: 1 }, { silent: true });
      }
    } else if (previewQuantity > 1) {
      setPreviewQuantity((prev) => prev - 1);
    }
  };

  // Loading / Error UI
  if (loading && !product) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <BackButton />
        <Box sx={{ textAlign: "center", py: 10 }}>
          <CircularProgress />
          <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
            Loading product details...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <BackButton />
        <Box sx={{ textAlign: "center", py: 10 }}>
          <Typography variant="h4" fontWeight="bold" color="text.primary">
            {error}
          </Typography>
          <Button
            onClick={() => navigate(-1)}
            variant="contained"
            sx={{ mt: 4 }}
          >
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  if (!product || !product.product) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <BackButton />
        <Box sx={{ textAlign: "center", py: 10 }}>
          <Typography variant="h4" fontWeight="bold" color="text.primary">
            Please Sign-in to view the details
          </Typography>
          <Button
            onClick={() => navigate(-1)}
            variant="contained"
            sx={{ mt: 4 }}
          >
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  const productItem = product.product;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <BackButton />

      <Grid container spacing={6} sx={{ mb: 8 }} className="ob-pd-grid">
        {/* Image Gallery — or, while trying on, the mirror in its place */}
        <Grid item xs={12} md={tryOnOpen ? 12 : 6}>
          {tryOnOpen ? (
            <TryOnStage
              key={tryOnSession}
              productId={productId}
              productTitle={productItem?.title}
              image={
                allImages[currentImageIndex]
                  ? cleanImageUrl(allImages[currentImageIndex].url)
                  : ""
              }
              count={allImages.length}
              index={currentImageIndex}
              onPrev={handlePrevImage}
              onNext={handleNextImage}
              autoStart={tryOnAutoStart}
              onExit={closeTryOn}
            />
          ) : (
          <Box sx={{ display: "flex", gap: 2 }}>
            {/* Thumbnails Column - Made Scrollable */}
            <Box
              sx={{
                width: 80,
                overflowY: "auto",
                maxHeight: "calc(100vh - 200px)", // Adjust this value based on your layout needs
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(0,0,0,0.2) rgba(0,0,0,0.1)",
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "rgba(0,0,0,0.1)",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0,0,0,0.2)",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: "rgba(0,0,0,0.3)",
                },
              }}
            >
              <Stack spacing={1.5}>
                {allImages.map((image, index) => (
                  <ThumbnailImage
                    key={index}
                    selected={currentImageIndex === index}
                    onClick={() => setCurrentImageIndex(index)}
                  >
                    <img
                      src={cleanImageUrl(image.url)}
                      alt={`Thumbnail ${index + 1}`}
                      onError={(e) => {
                        e.target.src = placeholderImage(`thumb${index}`, 100, 120);
                      }}
                    />
                  </ThumbnailImage>
                ))}
              </Stack>
            </Box>

            {/* Main Image */}
            <Box sx={{ flex: 1 }}>
              <MainImageContainer
                onClick={() => {
                  openTryOn();
                }}
                sx={{ cursor: "pointer" }}
              >
                <MainImage
                  src={
                    allImages[currentImageIndex]
                      ? cleanImageUrl(allImages[currentImageIndex].url)
                      : ""
                  }
                  alt={product.title}
                  onError={(e) => {
                    const seed = (productId || "fallback").replace(/\s+/g, "");
                    e.target.src = placeholderImage(seed, 800, 600);
                  }}
                />

                {/* Navigation Arrows */}
                {allImages.length > 1 && (
                  <>
                    <NavButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePrevImage();
                      }}
                      sx={{ left: 16 }}
                    >
                      <ChevronLeftIcon />
                    </NavButton>
                    <NavButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNextImage();
                      }}
                      sx={{ right: 16 }}
                    >
                      <ChevronRightIcon />
                    </NavButton>
                  </>
                )}

                {/* Badges */}
                <Box sx={{ position: "absolute", top: 16, left: 16 }}>
                  <Chip
                    icon={
                      <Box component="span" sx={{ fontSize: "16px" }}>
                        ⭐
                      </Box>
                    }
                    label="Highly Rated"
                    size="small"
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      bgcolor: "white",
                      fontWeight: 500,
                      fontSize: "0.75rem",
                      boxShadow: 1,
                      height: "28px",
                      "& .MuiChip-icon": {
                        marginLeft: "8px",
                        marginRight: "-4px",
                      },
                    }}
                  />
                </Box>
                <Box
                  sx={{ position: "absolute", top: 16, right: 16 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    openTryOn();
                  }}
                >
                  <Chip
                    icon={
                      <Box component="span" sx={{ fontSize: "16px" }}>
                        📷
                      </Box>
                    }
                    label="Try-On"
                    size="small"
                    sx={{
                      bgcolor: "rgba(0, 0, 0, 0.8)",
                      color: "white",
                      fontWeight: 500,
                      fontSize: "0.75rem",
                      boxShadow: 1,
                      height: "28px",
                      cursor: "pointer",
                      "&:hover": {
                        bgcolor: "rgba(0, 0, 0, 0.9)",
                      },
                      "& .MuiChip-icon": {
                        marginLeft: "8px",
                        marginRight: "-4px",
                        color: "white",
                      },
                    }}
                  />
                </Box>
              </MainImageContainer>
            </Box>
          </Box>
          )}
        </Grid>

        {/* Product Info — below the try-on stage, centred, in try-on mode */}
        <Grid
          item
          xs={12}
          md={tryOnOpen ? 12 : 6}
          sx={
            tryOnOpen
              ? { maxWidth: { md: 780 }, mx: "auto", animation: "obRiseIn .6s .18s both" }
              : undefined
          }
        >
          <Stack spacing={3}>
            {/* Title & Rating */}
            <Box>
              <Typography
                variant="h4"
                component="h1"
                fontWeight={700}
                gutterBottom
              >
                {productItem.title}
              </Typography>

              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <Rating value={rating} readOnly precision={0.1} size="small" />
                <Typography variant="body2" color="text.secondary">
                  {rating} ({reviewCount} reviews)
                </Typography>
              </Box>

              {/* Price */}
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}
              >
                {isOnSale ? (
                  <>
                    <Box sx={{ display: "flex", alignItems: "baseline" }}>
                      <Typography
                        sx={{
                          fontSize: "1.75rem",
                          fontWeight: 700,
                          lineHeight: 1,
                          color: "#D32F2F",
                          letterSpacing: "-0.3px",
                        }}
                      >
                        ${Math.floor(displayPrice)}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "1.25rem",
                          fontWeight: 600,
                          marginLeft: "1px",
                          color: "#D32F2F",
                          lineHeight: 1,
                        }}
                      >
                        {displayPrice
                          .toFixed(2)
                          .slice(displayPrice.toFixed(2).indexOf("."))}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "1rem",
                        fontWeight: 400,
                        color: "#9E9E9E",
                        textDecoration: "line-through",
                      }}
                    >
                      ${originalPrice.toFixed(2)}
                    </Typography>
                  </>
                ) : (
                  <Box sx={{ display: "flex", alignItems: "baseline" }}>
                    <Typography
                      sx={{
                        fontSize: "1.75rem",
                        fontWeight: 700,
                        lineHeight: 1,
                        color: "#000",
                        letterSpacing: "-0.3px",
                      }}
                    >
                      ${Math.floor(displayPrice)}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "1.25rem",
                        fontWeight: 600,
                        marginLeft: "1px",
                        color: "#000",
                        lineHeight: 1,
                      }}
                    >
                      {displayPrice
                        .toFixed(2)
                        .slice(displayPrice.toFixed(2).indexOf("."))}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Typography variant="body1" color="text.secondary" paragraph>
                {productItem.description || "No description available."}
              </Typography>
            </Box>

            {/* Color Selector */}
            {colorList.length > 0 && (
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Color{" "}
                  <Typography
                    component="span"
                    color="text.secondary"
                    fontWeight={400}
                  >
                    {selectedColor}
                  </Typography>
                </Typography>
                <Stack direction="row" spacing={1.5}>
                  {colorList.map((color, index) => (
                    <ColorCircle
                      key={index}
                      selected={selectedColor === color}
                      colorvalue={getColorHex(color)}
                      onClick={() => setSelectedColor(color)}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Size Selector */}
            {sizeList.length > 0 && (
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Size
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {sizeList.map((size, index) => (
                    <SizeButton
                      key={index}
                      selected={selectedSize === size}
                      onClick={() => setSelectedSize(size)}
                    >
                      {size}
                    </SizeButton>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Quantity */}
            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Quantity
              </Typography>
              <Stack direction="row" alignItems="center" spacing={2}>
                <QuantitySelector>
                  <QuantityButton
                    onClick={decrementQuantity}
                    disabled={quantity <= 1}
                  >
                    <RemoveIcon fontSize="small" />
                  </QuantityButton>
                  <Typography
                    sx={{
                      px: 3,
                      fontWeight: 600,
                      minWidth: 40,
                      textAlign: "center",
                    }}
                  >
                    {quantity}
                  </Typography>
                  <QuantityButton onClick={incrementQuantity}>
                    <AddIcon fontSize="small" />
                  </QuantityButton>
                </QuantitySelector>
                <Typography variant="body2" color="text.secondary">
                  Only {Math.min(9, Math.floor(Math.random() * 10) + 1)} items
                  left in stock
                </Typography>
              </Stack>
            </Box>

            {/* Action Buttons */}
            <Stack direction="row" spacing={1.5}>
              <Button
                onClick={handleAddToCart}
                disabled={!selectedSize}
                variant="contained"
                fullWidth
                startIcon={<ShoppingCartIcon />}
                sx={{
                  py: 1.5,
                  fontSize: "1rem",
                  textTransform: "none",
                  bgcolor: "grey.900",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "grey.800" },
                  ...(isAlreadyInCart && {
                    bgcolor: "success.main",
                    "&:hover": { bgcolor: "success.dark" },
                  }),
                }}
              >
                {isAlreadyInCart
                  ? "✓ Added to Cart"
                  : selectedSize
                  ? "Add to Cart"
                  : "Select a Size"}
              </Button>
              <WishlistToggle product={productItem} />
              <IconButton sx={{ border: "1px solid #e0e0e0" }}>
                <ShareIcon />
              </IconButton>
            </Stack>

            {/* Features */}
            <Stack direction="row" spacing={3} sx={{ pt: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LocalShippingIcon fontSize="small" />
                <Typography variant="body2">Free Shipping</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <SecurityIcon fontSize="small" />
                <Typography variant="body2">Secure Payment</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <RefreshIcon fontSize="small" />
                <Typography variant="body2">Easy Returns</Typography>
              </Box>
            </Stack>

            <Divider sx={{ my: 2 }} />

            {/* Product Details */}
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    SKU
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {productItem.id || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Category
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {productItem.categories?.[0]?.split(" > ").pop() || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Material
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {productItem.attributes?.fabric || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Fit
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {productItem.attributes?.fit || "N/A"}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </Grid>
      </Grid>

      {/* Other Variants */}
      {product.variants && product.variants.length > 1 && (
        <Box sx={{ mt: 8 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Other Variants
          </Typography>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            {product.variants.slice(1).map((variant, index) => {
              const vPriceInfo = variant.price_info || {};
              const vOriginalPrice = parseFloat(
                variant.original_price || vPriceInfo.originalPrice || 0
              );
              const vDiscountedPrice =
                variant.discounted_price !== undefined
                  ? parseFloat(variant.discounted_price)
                  : parseFloat(vPriceInfo.price || vOriginalPrice);
              const vIsOnPromotion =
                vOriginalPrice > 0 && vDiscountedPrice < vOriginalPrice;

              const vImages = (
                Array.isArray(variant.images) ? variant.images : []
              )
                .map(cleanImageUrl)
                .filter(Boolean);
              const vImage =
                vImages[0] ||
                placeholderImage(`variant-${index}`, 400, 300);

              return (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <ProductCard>
                    <CardMedia
                      component="img"
                      height="200"
                      image={vImage}
                      alt={variant.title || productItem.title}
                      sx={{ objectFit: "cover" }}
                      onError={(e) => {
                        e.target.src = placeholderImage(`variant-${index}`, 400, 300);
                      }}
                    />
                    <CardContent>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        gutterBottom
                      >
                        {variant.title || productItem.title}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        {vIsOnPromotion ? (
                          <>
                            <Typography
                              variant="h6"
                              fontWeight={700}
                              color="error.main"
                            >
                              ${vDiscountedPrice.toFixed(2)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ textDecoration: "line-through" }}
                            >
                              ${vOriginalPrice.toFixed(2)}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="h6" fontWeight={700}>
                            ${vDiscountedPrice.toFixed(2)}
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </ProductCard>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

    </Container>
  );
}
